import { useMemo } from "react";
import { useAppSelector } from "redux/hooks";
import {
  selectFeatureMap,
  selectFeaturesLoaded,
  selectFeaturesLoading,
} from "redux/featuresSlice";

export type FeatureState =
  | { status: "available" }
  | { status: "admin-disabled"; disabledMessage?: string }
  | { status: "not-in-plan" };

export const useFeature = (key: string): boolean => {
  const byKey = useAppSelector(selectFeatureMap);
  const loaded = useAppSelector(selectFeaturesLoaded);
  return useMemo(() => !loaded || byKey[key]?.enabled === true, [byKey, loaded, key]);
};

/**
 * Strict variant of `useFeature`: true only once the accessible-features list
 * has loaded **and** lists `key` with `enabled: true`. For features that are
 * hidden rather than shown locked (e.g. identity verification), where
 * `useFeature`'s optimistic "available until loaded" would flash the control.
 */
export const useFeatureEnabled = (key: string): boolean => {
  const byKey = useAppSelector(selectFeatureMap);
  const loaded = useAppSelector(selectFeaturesLoaded);
  return loaded && byKey[key]?.enabled === true;
};

export const useFeatureState = (key: string): FeatureState => {
  const byKey = useAppSelector(selectFeatureMap);
  const loaded = useAppSelector(selectFeaturesLoaded);
  return useMemo(() => {
    if (!loaded) return { status: "available" };
    const f = byKey[key];
    if (!f) return { status: "not-in-plan" };
    if (!f.enabled)
      return { status: "admin-disabled", disabledMessage: f.disabledMessage };
    return { status: "available" };
  }, [byKey, loaded, key]);
};

export interface FeatureBag {
  has: (key: string) => boolean;
  state: (key: string) => FeatureState;
  loaded: boolean;
  loading: boolean;
}

export const useFeatures = (): FeatureBag => {
  const byKey = useAppSelector(selectFeatureMap);
  const loaded = useAppSelector(selectFeaturesLoaded);
  const loading = useAppSelector(selectFeaturesLoading);

  const has = useMemo(
    () => (key: string) => !loaded || byKey[key]?.enabled === true,
    [byKey, loaded]
  );

  const state = useMemo(
    () =>
      (key: string): FeatureState => {
        if (!loaded) return { status: "available" };
        const f = byKey[key];
        if (!f) return { status: "not-in-plan" };
        if (!f.enabled)
          return {
            status: "admin-disabled",
            disabledMessage: f.disabledMessage,
          };
        return { status: "available" };
      },
    [byKey, loaded]
  );

  return { has, state, loaded, loading };
};
